import {Policy} from '../../types-repository';

export const MOUNT_ACTION = 'contentplatform:repository:Mutation/MountEditingWorkspace';
const POOL_RESOURCE = 'contentplatform:pool:input.pool/';

/** fnmatch-style: `*` any run, `?` one character — as the authorizer and the mount Lambda match pools. */
function globMatch(pattern: string, value: string): boolean {
  const regex = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp(`^${regex}$`).test(value);
}

/** The `input.pool` patterns of the caller's statements for the mount action with the given effect. */
function poolPatterns(policies: Policy[] | null | undefined, effect: string): string[] {
  const patterns: string[] = [];
  for (const policy of policies ?? []) {
    for (const statement of policy.statements) {
      if (statement.effect !== effect || !statement.actions.includes(MOUNT_ACTION)) {
        continue;
      }
      for (const resource of statement.resources ?? []) {
        if (resource === '*') {
          patterns.push('*');
        } else if (resource.startsWith(POOL_RESOURCE)) {
          patterns.push(resource.slice(POOL_RESOURCE.length));
        }
      }
    }
  }
  return patterns;
}

/**
 * The `pool` to send when mounting a workspace of `workspacePool` on a host of `hostPool`, or null when the
 * caller's policies allow no such mount.
 *
 * Two checks decide a mount, and the value must pass both:
 * - the authorizer: some allow statement's `input.pool` pattern matches the value, and no deny statement's does;
 * - the mount Lambda: the value, as a pattern, matches the volume's pool and the host's pool.
 * Candidates, most specific first: the workspace's own pool (when the host has the same pool), then each pattern a
 * policy grants (an admin's `*` covers another pool's host; an operator's names only its own pool).
 */
export function mountPool(policies: Policy[] | null | undefined, workspacePool: string | null | undefined,
                          hostPool: string | null | undefined): string | null {
  if (!workspacePool || !hostPool) {
    return null;
  }
  const allowed = poolPatterns(policies, 'allow');
  const denied = poolPatterns(policies, 'deny');
  const candidates = workspacePool === hostPool ? [workspacePool, ...allowed] : allowed;
  return candidates.find(value =>
    allowed.some(pattern => globMatch(pattern, value)) && !denied.some(pattern => globMatch(pattern, value))
    && globMatch(value, workspacePool) && globMatch(value, hostPool)) ?? null;
}
